import { useCallback, useEffect, useState } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material'

const STATUSES = [
  { value: 'open', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'customer_responded', label: 'Customer responded' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'closed', label: 'Closed' }
]

const formatDate = (value) => {
  if (!value) return '-'

  const date = new Date(value)

  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('sk-SK')
}

const Field = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mb: 0.5 }}
    >
      {label}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap'
      }}
    >
      {value === null ||
      value === undefined ||
      value === ''
        ? '-'
        : String(value)}
    </Typography>
  </Box>
)

const SupportTicketModal = ({
  ticketId,
  onClose,
  onUpdated
}) => {
  const open = ticketId !== null && ticketId !== undefined

  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [status, setStatus] = useState('')
  const [messageType, setMessageType] = useState('note')
  const [messageBody, setMessageBody] = useState('')

  const [agents, setAgents] = useState([])
  const [selectedAgent, setSelectedAgent] = useState('')
  const [savingAgent] = useState(false)

  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const loadDetail = useCallback(async () => {
    if (ticketId === null || ticketId === undefined) {
      return
    }

    setLoading(true)

    try {
      const response = await fetchWithAuth(
        `/support/tickets/${ticketId}`,
        { cache: 'no-store' }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      setDetail(data)
      setStatus(data.ticket?.status || '')
      setSelectedAgent(data.ticket?.assigned_to ?? '')
    } catch (err) {
      setError(
        `Nepodarilo sa načítať tiket: ${err.message}`
      )
    } finally {
      setLoading(false)
    }
  }, [ticketId])

  const loadAgents = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        '/support/agents',
        { cache: 'no-store' }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      setAgents(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(
        `Nepodarilo sa načítať support agentov: ${err.message}`
      )
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setDetail(null)
      setStatus('')
      setSelectedAgent('')
      setAgents([])
      setMessageBody('')
      setMessageType('note')
      setError('')
      setSuccess('')
      return
    }

    setDetail(null)
    setError('')
    setSuccess('')
    setMessageBody('')
    setMessageType('note')
    setSelectedAgent('')
    setAgents([])

    loadDetail()
    loadAgents()
  }, [open, loadDetail, loadAgents])

  const saveChanges = async () => {
    if (!detail?.ticket || busy || (!statusChanged && !agentChanged)) return

    setSaving(true)
    setError('')
    setSuccess('')

    try {
      if (statusChanged) {
        const response = await fetchWithAuth(
          `/support/tickets/${ticketId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status })
          }
        )

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          throw new Error(data.message || `HTTP ${response.status}`)
        }
      }

      if (agentChanged) {
        const response = await fetchWithAuth(
          `/support/tickets/${ticketId}/agent`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              agentId: selectedAgent === '' ? null : Number(selectedAgent)
            })
          }
        )

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          throw new Error(data.message || `HTTP ${response.status}`)
        }
      }

      await loadDetail()
      onUpdated?.()
      setSuccess('Zmeny boli uložené.')
    } catch (err) {
      await loadDetail()
      setError(`Nepodarilo sa uložiť zmeny: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const saveMessage = async () => {
    if (!messageBody.trim() || saving || savingAgent) return

    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetchWithAuth(
        `/support/tickets/${ticketId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: messageType,
            body: messageBody.trim()
          })
        }
      )

      const data = await response.json().catch(() => ({}))

      if (data.saved) {
        setMessageBody('')

        await loadDetail()
        onUpdated?.()

        if (data.emailed) {
          setSuccess(
            'Odpoveď bola uložená do tiketu a odoslaná zákazníkovi.'
          )
        } else if (messageType === 'note') {
          setSuccess(
            'Interná poznámka bola uložená do tiketu.'
          )
        } else {
          setError(
            'Odpoveď bola uložená do tiketu, ale odoslanie e-mailu zlyhalo.'
          )
        }

        return
      }

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP ${response.status}`
        )
      }

      throw new Error('Neplatná odpoveď servera')
    } catch (err) {
      setError(
        `Nepodarilo sa uložiť správu: ${err.message}`
      )
    } finally {
      setSaving(false)
    }
  }

  const ticket = detail?.ticket
  const messages = Array.isArray(detail?.messages)
    ? detail.messages
    : []

  const statusChanged =
    ticket && status !== ticket.status

  const agentChanged =
    ticket &&
    String(selectedAgent) !== String(ticket.assigned_to ?? '')

  const busy = saving || savingAgent

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) onClose?.()
      }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(1500px, 96vw)',
          maxWidth: '96vw',
          height: 'min(920px, 94vh)',
          maxHeight: '94vh',
          m: 1,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            component="div"
            fontWeight={700}
          >
            {ticket
              ? `Ticket ${ticket.ticket_number}`
              : 'Ticket'}
          </Typography>

          {ticket && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {ticket.subject}
            </Typography>
          )}
        </Box>


      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 2, md: 3 },
          flex: 1,
          overflowY: 'auto'
        }}
      >
        <Stack spacing={2.5}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          {loading && !detail ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 8
              }}
            >
              <CircularProgress />
            </Box>
          ) : ticket ? (
            <>
              <Paper
                variant="outlined"
                sx={{ p: 2.5 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mb: 2 }}
                >
                  Údaje tiketu
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(4, minmax(0, 1fr))'
                    },
                    gap: 2.5
                  }}
                >
                  <Field
                    label="ID tiketu"
                    value={ticket.id}
                  />

                  <Field
                    label="Číslo tiketu"
                    value={ticket.ticket_number}
                  />

                  <Field
                    label="ID používateľa"
                    value={ticket.user_id}
                  />

                  <Field
                    label="E-mail zákazníka"
                    value={ticket.email}
                  />

                  <Field
                    label="Predmet"
                    value={ticket.subject}
                  />

                  <Field
                    label="Dátum vytvorenia"
                    value={formatDate(ticket.created_at)}
                  />

                  <Field
                    label="Posledná aktivita"
                    value={formatDate(ticket.updated_at)}
                  />

                  <Field
                    label="Počet správ"
                    value={messages.length}
                  />
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row'
                  }}
                  spacing={2}
                  alignItems={{
                    xs: 'stretch',
                    sm: 'center'
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{ minWidth: 240 }}
                  >
                    <InputLabel id="support-ticket-status-label">
                      Stav tiketu
                    </InputLabel>

                    <Select
                      labelId="support-ticket-status-label"
                      value={status}
                      label="Stav tiketu"
                      onChange={(event) => {
                        setStatus(event.target.value)
                      }}
                      disabled={busy}
                    >
                      {STATUSES.map((item) => (
                        <MenuItem
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 240 }}
                  >
                    <InputLabel id="support-ticket-agent-label">
                      Support agent
                    </InputLabel>

                    <Select
                      labelId="support-ticket-agent-label"
                      value={selectedAgent}
                      label="Support agent"
                      onChange={(event) => {
                        setSelectedAgent(event.target.value)
                      }}
                      disabled={busy}
                    >
                      <MenuItem value="">
                        Nepriradené
                      </MenuItem>

                      {agents.map((agent) => (
                        <MenuItem
                          key={agent.id}
                          value={agent.id}
                        >
                          {`${agent.meno} ${agent.priezvisko}`.trim()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{ p: 2.5 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mb: 2 }}
                >
                  História komunikácie
                </Typography>

                {messages.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Zatiaľ žiadne správy.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {messages.map((message) => {
                      const isPrivate =
                        message.is_private === true

                      const isIncoming =
                        message.direction === 'incoming'

                      return (
                        <Paper
                          key={message.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: isPrivate
                              ? 'rgba(255, 193, 7, 0.08)'
                              : isIncoming
                                ? 'action.hover'
                                : 'background.paper',
                            borderLeft: '4px solid',
                            borderLeftColor: isPrivate
                              ? 'warning.main'
                              : isIncoming
                                ? 'info.main'
                                : 'success.main'
                          }}
                        >
                          <Stack
                            direction={{
                              xs: 'column',
                              sm: 'row'
                            }}
                            justifyContent="space-between"
                            alignItems={{
                              xs: 'flex-start',
                              sm: 'center'
                            }}
                            spacing={1}
                            sx={{ mb: 1.5 }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                label={
                                  isPrivate
                                    ? 'Private note'
                                    : isIncoming
                                      ? 'Incoming'
                                      : 'Reply'
                                }
                                color={
                                  isPrivate
                                    ? 'warning'
                                    : isIncoming
                                      ? 'info'
                                      : 'success'
                                }
                              />

                              {!isPrivate &&
                                !isIncoming &&
                                message.delivery_status && (
                                  <Chip
                                    size="small"
                                    label={
                                      message.delivery_status === 'sent'
                                        ? 'Email sent'
                                        : message.delivery_status === 'failed'
                                          ? 'Email failed'
                                          : 'Email pending'
                                    }
                                    color={
                                      message.delivery_status === 'sent'
                                        ? 'success'
                                        : message.delivery_status === 'failed'
                                          ? 'error'
                                          : 'warning'
                                    }
                                  />
                                )}

                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  overflowWrap: 'anywhere'
                                }}
                              >
                                {message.sender_email || '-'}
                              </Typography>
                            </Stack>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(message.created_at)}
                            </Typography>
                          </Stack>

                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere'
                            }}
                          >
                            {message.body}
                          </Typography>

                          {message.delivery_status === 'failed' &&
                            message.delivery_error && (
                              <Alert
                                severity="error"
                                sx={{ mt: 2 }}
                              >
                                {message.delivery_error}
                              </Alert>
                            )}
                        </Paper>
                      )
                    })}
                  </Stack>
                )}
              </Paper>

              <Paper
                variant="outlined"
                sx={{ p: 2.5 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mb: 2 }}
                >
                  Pridať správu
                </Typography>

                <Tabs
                  value={messageType}
                  onChange={(_, value) => {
                    setMessageType(value)
                    setError('')
                    setSuccess('')
                  }}
                  sx={{
                    mb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Tab
                    value="note"
                    label="Private note"
                  />

                  <Tab
                    value="reply"
                    label="Reply"
                  />
                </Tabs>

                {messageType === 'note' ? (
                  <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                  >
                    Interná poznámka sa uloží iba do tiketu.
                    Zákazníkovi sa neodošle e-mail.
                  </Alert>
                ) : (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                  >
                    Odpoveď sa najprv uloží do tiketu
                    a následne odošle e-mailom na{' '}
                    <strong>{ticket.email}</strong>.
                  </Alert>
                )}

                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  maxRows={16}
                  label={
                    messageType === 'note'
                      ? 'Interná poznámka'
                      : 'Odpoveď zákazníkovi'
                  }
                  placeholder={
                    messageType === 'note'
                      ? 'Napíšte internú poznámku...'
                      : 'Napíšte odpoveď zákazníkovi...'
                  }
                  value={messageBody}
                  onChange={(event) => {
                    setMessageBody(event.target.value)
                  }}
                  disabled={busy}
                  inputProps={{ maxLength: 50000 }}
                />

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="contained"
                    color={
                      messageType === 'note'
                        ? 'warning'
                        : 'primary'
                    }
                    disabled={
                      busy ||
                      !messageBody.trim()
                    }
                    onClick={saveMessage}
                  >
                    {saving ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                      />
                    ) : messageType === 'note' ? (
                      'Uložiť internú poznámku'
                    ) : (
                      'Uložiť a odoslať odpoveď'
                    )}
                  </Button>
                </Stack>
              </Paper>
            </>
          ) : (
            !loading &&
            !error && (
              <Typography color="text.secondary">
                Tiket nebol nájdený.
              </Typography>
            )
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Button
          onClick={loadDetail}
          disabled={loading || busy || !ticket}
        >
          Obnoviť
        </Button>

        <Button
          variant="outlined"
          onClick={statusChanged || agentChanged ? saveChanges : onClose}
          disabled={busy}
          sx={statusChanged || agentChanged ? {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' }
          } : undefined}
        >
          {statusChanged || agentChanged ? 'Uložiť' : 'Zavrieť'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SupportTicketModal
